import { fetchAuthSession, signOut } from 'aws-amplify/auth';

const API_URL = import.meta.env.VITE_API_URL || '';

async function getToken() {
  const session = await fetchAuthSession();
  const token = session.tokens?.idToken?.toString();
  if (!token) {
    await forceSignOut();
    throw new Error('Session expired');
  }
  return token;
}

async function forceSignOut() {
  try {
    await signOut();
  } catch {
    // Best-effort cleanup
  }
  window.location.href = '/login';
}

async function request(method, path, { body, headers = {} } = {}) {
  const token = await getToken();

  const opts = {
    method,
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
      ...headers,
    },
  };

  if (body) opts.body = JSON.stringify(body);

  const res = await fetch(`${API_URL}${path}`, opts);

  if (res.status === 401) {
    await forceSignOut();
    throw new Error('Session expired');
  }

  if (res.status === 204) return null;

  const data = await res.json().catch(() => null);

  if (!res.ok) {
    const err = new Error(data?.message || `Request failed: ${res.status}`);
    err.status = res.status;
    err.data = data;
    throw err;
  }

  return data;
}

// ─── Applications ─────────────────────────────────────────────────────────────

export function fetchApplications(updatedAfter) {
  const params = updatedAfter ? `?updatedAfter=${encodeURIComponent(updatedAfter)}` : '';
  return request('GET', `/applications${params}`);
}

export function fetchApplication(appId) {
  return request('GET', `/applications/${appId}`);
}

export function createApplication(data) {
  return request('POST', '/applications', {
    body: data,
    headers: { 'X-Idempotency-Key': crypto.randomUUID() },
  });
}

export function updateApplication(appId, data) {
  return request('PUT', `/applications/${appId}`, { body: data });
}

export function deleteApplication(appId) {
  return request('DELETE', `/applications/${appId}`);
}

// ─── Status ───────────────────────────────────────────────────────────────────

export function updateStatus(appId, newStatus, columnOrder) {
  return request('PATCH', `/applications/${appId}/status`, {
    body: { newStatus, ...(columnOrder !== undefined && { columnOrder }) },
  });
}

export function batchUpdateStatus(items) {
  return request('POST', '/applications/batch-status', { body: { items } });
}

// ─── Reorder ──────────────────────────────────────────────────────────────────

export function updateOrder(appId, columnOrder) {
  return request('PATCH', `/applications/${appId}/order`, { body: { columnOrder } });
}

export function batchReorder(items) {
  return request('PATCH', '/applications/batch-reorder', { body: { items } });
}

// ─── Templates ────────────────────────────────────────────────────────────────

export function fetchTemplates() {
  return request('GET', '/templates');
}

export function createTemplate(data) {
  return request('POST', '/templates', {
    body: data,
    headers: { 'X-Idempotency-Key': crypto.randomUUID() },
  });
}

export function updateTemplate(templateId, data) {
  return request('PUT', `/templates/${templateId}`, { body: data });
}

export function deleteTemplate(templateId) {
  return request('DELETE', `/templates/${templateId}`);
}

// ─── Quick Clip ───────────────────────────────────────────────────────────────

export function quickClip(data) {
  return request('POST', '/applications/quick-clip', {
    body: data,
    headers: { 'X-Idempotency-Key': crypto.randomUUID() },
  });
}

// ─── Account ──────────────────────────────────────────────────────────────────

export function deleteAccount() {
  return request('DELETE', '/account');
}

// ─── Share ────────────────────────────────────────────────────────────────────

export function generateShareToken() {
  return request('POST', '/share');
}

export function revokeShareToken() {
  return request('DELETE', '/share');
}

export function updateProfile(data) {
  return request('PATCH', '/profile', { body: data });
}

export async function fetchSharedSummary(token) {
  const res = await fetch(`${API_URL}/shared/${token}`);
  if (!res.ok) return null;
  return res.json();
}

// ─── Stories ──────────────────────────────────────────────────────────────────

export function fetchStories() {
  return request('GET', '/stories');
}

export function createStory(data) {
  return request('POST', '/stories', { body: data });
}

export function updateStory(storyId, data) {
  return request('PUT', `/stories/${storyId}`, { body: data });
}

export function deleteStory(storyId) {
  return request('DELETE', `/stories/${storyId}`);
}

export function addPracticeLog(storyId, data) {
  return request('POST', `/stories/${storyId}/practice`, { body: data });
}

export function resetPracticeHistory(storyId) {
  return request('DELETE', `/stories/${storyId}/practice`);
}

// ─── Goals ────────────────────────────────────────────────────────────────────

export function fetchGoals() {
  return request('GET', '/goals');
}

export function createGoal(data) {
  return request('POST', '/goals', { body: data });
}

export function updateGoal(goalId, data) {
  return request('PUT', `/goals/${goalId}`, { body: data });
}

export function deleteGoal(goalId) {
  return request('DELETE', `/goals/${goalId}`);
}

export function incrementGoal(goalId) {
  return request('PATCH', `/goals/${goalId}/increment`);
}
